/**
 * @fileoverview Página responsável por listar e acompanhar leads externos.
 */

import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Drawer from "@material-ui/core/Drawer";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";

import MainContainer from "../../components/MainContainer";
import KanbanBoard from "../../components/KanbanBoard";
import CrmCalendarView from "../../components/CrmCalendarView";
import LeadDetailModal from "../../components/LeadDetailModal";
import StartConversationModal from "../../components/StartConversationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import LeadSettingsContent from "../../components/LeadSettingsContent";
import CreateLeadModal from "../../components/CreateLeadModal";
import CrmActivityLogDrawer from "../../components/CrmActivityLog";
import BoardTemplateModal from "../../components/BoardTemplateModal";
import BoardShareModal from "../../components/BoardShareModal";
import { UserAvatar } from "../../components/LeadShared";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { isInternalOnly } from "../../utils/accessScope";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useExternalLeadsStore } from "../../stores/useExternalLeadsStore";

import {
  Pencil,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  History,
  Settings,
  Users as UsersIcon,
  Check,
  X,
  ChevronDown,
  LayoutGrid,
  CalendarDays,
  Layers,
  Share2,
} from "lucide-react";

const BOARD_COLORS = [
  "#5B7CFF", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#f97316", "#14b8a6", "#6366f1"
];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
      padding: theme.spacing(2.5, 3, 1.5),
      background: dark ? "#0A0A0A" : "#F8FAFC",
      [theme.breakpoints.down("xs")]: { padding: theme.spacing(1.5, 1.5, 1) },
    },

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(2),
      gap: 12,
      flexShrink: 0,
      flexWrap: "wrap",
      [theme.breakpoints.down("xs")]: { marginBottom: theme.spacing(1.5), gap: 10 },
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: { flex: "1 1 100%" },
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      marginBottom: 2,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    eyebrowDot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: dark ? "#818CF8" : "#3D5CFD",
    },
    title: {
      fontSize: "1.6rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: dark ? "#E8EAED" : "#0F172A",
      lineHeight: 1.1,
      [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: 4,
      borderRadius: 12,
      background: dark ? "rgba(14,16,22,0.85)" : "#FFFFFF",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
    },
    iconBtn: {
      width: 34,
      height: 34,
      borderRadius: 9,
      padding: 0,
      color: dark ? "#9CA3AF" : "#475569",
      transition: "background .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.05)",
        color: dark ? "#E8EAED" : "#0F172A",
      },
      "&:disabled": {
        opacity: 0.5,
      },
    },
    iconBtnActive: {
      background: dark ? "rgba(99,102,241,0.12)" : "rgba(61,92,253,0.10)",
      color: dark ? "#A5B4FC" : "#3D5CFD",
      "&:hover": {
        background: dark ? "rgba(99,102,241,0.18)" : "rgba(61,92,253,0.14)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
      },
    },

    viewSwitch: {
      display: "flex",
      alignItems: "center",
      gap: 3,
      padding: 4,
      borderRadius: 12,
      background: dark ? "rgba(14,16,22,0.85)" : "#FFFFFF",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
    },
    viewSwitchBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      height: 34,
      padding: "0 13px",
      borderRadius: 9,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#9CA3AF" : "#64748B",
      transition: "background .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.05)",
        color: dark ? "#E8EAED" : "#0F172A",
      },
    },
    viewSwitchBtnActive: {
      background: "rgba(71,71,71,0.22)",
      color: "#ffffff",
      "&:hover": {
        background: "rgba(134,134,134,0.20)",
        color: "#ffffff",
      },
    },

    boardTabs: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: theme.spacing(2),
      flexShrink: 0,
      overflowX: "auto",
      padding: "4px 2px",
      "&::-webkit-scrollbar": { height: 0 },
    },
    boardTab: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 12px",
      borderRadius: 10,
      cursor: "pointer",
      transition: "background .15s, color .15s, box-shadow .15s",
      border: "1px solid transparent",
      whiteSpace: "nowrap",
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#9CA3AF" : "#64748B",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.04)",
        color: dark ? "#E8EAED" : "#0F172A",
      },
    },
    boardTabActive: {
      background: dark ? "rgba(14,16,22,0.85)" : "#FFFFFF",
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
      color: dark ? "#E8EAED" : "#0F172A",
      boxShadow: dark
        ? "0 1px 0 rgba(255,255,255,0.03) inset"
        : "0 1px 2px rgba(15,23,42,0.04)",
      "&:hover": {
        background: dark ? "rgba(14,16,22,0.95)" : "#FFFFFF",
        color: dark ? "#E8EAED" : "#0F172A",
      },
    },
    boardTabDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "0 0 0 2px rgba(0,0,0,0.04)",
    },
    boardTabCount: {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 6px",
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.05)",
      color: dark ? "#9CA3AF" : "#64748B",
      minWidth: 22,
      textAlign: "center",
      lineHeight: 1,
    },
    boardTabAdminIcons: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginLeft: 4,
      opacity: 0.65,
    },
    boardTabIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: 6,
      cursor: "pointer",
      color: "currentColor",
      transition: "background .12s, color .12s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
      },
    },
    boardTabIconDanger: {
      "&:hover": {
        background: "rgba(239,68,68,0.14)",
        color: "#ef4444",
      },
    },
    addBoardBtn: {
      padding: "7px 12px",
      borderRadius: 10,
      cursor: "pointer",
      transition: "background .15s, color .15s, border-color .15s",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#9CA3AF" : "#64748B",
      border: dark ? "1px dashed rgba(255,255,255,0.10)" : "1px dashed rgba(15,23,42,0.14)",
      background: "transparent",
      marginLeft: 4,
      fontFamily: "inherit",
      "&:hover": {
        color: dark ? "#A5B4FC" : "#3D5CFD",
        borderColor: dark ? "rgba(99,102,241,0.28)" : "rgba(61,92,253,0.28)",
        background: dark ? "rgba(99,102,241,0.04)" : "rgba(61,92,253,0.04)",
      },
    },

    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: theme.spacing(1.5),
      flexShrink: 0,
      flexWrap: "wrap",
      [theme.breakpoints.down("xs")]: {
        flexWrap: "nowrap",
        gap: 6,
        marginBottom: theme.spacing(1),
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
      },
    },
    searchWrap: {
      position: "relative",
      flex: "0 1 320px",
      maxWidth: 320,
      minWidth: 200,
      [theme.breakpoints.down("xs")]: { flex: "1 1 120px", maxWidth: "none", minWidth: 96 },
    },
    searchField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: 38,
        borderRadius: 11,
        background: dark ? "rgba(14,16,22,0.85)" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        transition: "border-color .15s, background .15s, box-shadow .15s",
        "& fieldset": { border: "none" },
        "&:hover": {
          borderColor: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.14)",
        },
        "&.Mui-focused": {
          borderColor: dark ? "rgba(99,102,241,0.42)" : "rgba(61,92,253,0.42)",
          background: dark ? "rgba(14,16,22,0.95)" : "#FFFFFF",
          boxShadow: dark
            ? "0 0 0 1px rgba(99,102,241,0.08)"
            : "0 0 0 1px rgba(61,92,253,0.08)",
        },
      },
      "& .MuiOutlinedInput-input": {
        padding: "0 36px 0 36px",
        fontSize: 13,
        height: 38,
        color: dark ? "#E8EAED" : "#0F172A",
        "&::placeholder": {
          color: dark ? "#6B7280" : "#94A3B8",
          opacity: 1,
        },
      },
    },
    searchIcon: {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#6B7280" : "#94A3B8",
      pointerEvents: "none",
      zIndex: 1,
    },
    searchClearBtn: {
      position: "absolute",
      right: 6,
      top: "50%",
      transform: "translateY(-50%)",
      width: 24,
      height: 24,
      borderRadius: 6,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "#9CA3AF" : "#94A3B8",
      transition: "background .12s, color .12s",
      zIndex: 1,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
        color: dark ? "#E8EAED" : "#0F172A",
      },
    },

    filterTrigger: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      height: 38,
      padding: "0 10px 0 12px",
      borderRadius: 11,
      [theme.breakpoints.down("xs")]: { flexShrink: 0, height: 36, gap: 6 },
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
      background: dark ? "rgba(14,16,22,0.85)" : "#FFFFFF",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#E8EAED" : "#0F172A",
      transition: "border-color .15s, background .15s, box-shadow .15s",
      "&:hover": {
        borderColor: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.14)",
      },
      "&:focus-visible": {
        outline: "none",
        borderColor: dark ? "rgba(99,102,241,0.42)" : "rgba(61,92,253,0.42)",
        boxShadow: dark
          ? "0 0 0 1px rgba(99,102,241,0.08)"
          : "0 0 0 1px rgba(61,92,253,0.08)",
      },
    },
    filterTriggerActive: {
      borderColor: dark ? "rgba(99,102,241,0.35)" : "rgba(61,92,253,0.42)",
      background: dark ? "rgba(99,102,241,0.06)" : "rgba(61,92,253,0.06)",
      color: dark ? "#A5B4FC" : "#3D5CFD",
    },
    filterLabel: {
      maxWidth: 160,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    filterClearBtn: {
      width: 18,
      height: 18,
      borderRadius: 5,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "currentColor",
      opacity: 0.7,
      "&:hover": {
        background: "rgba(239,68,68,0.14)",
        color: "#ef4444",
        opacity: 1,
      },
    },

    pickerPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 12,
        background: dark ? "rgba(14,16,22,0.98)" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.7), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        maxHeight: 420,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginTop: 6,
      },
    },
    pickerSearchWrap: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.06)",
    },
    pickerSearchIcon: { color: dark ? "#6B7280" : "#94A3B8", flexShrink: 0 },
    pickerSearchInput: {
      flex: 1,
      border: "none",
      background: "transparent",
      outline: "none",
      fontSize: 13,
      fontFamily: "inherit",
      color: dark ? "#E8EAED" : "#0F172A",
      "&::placeholder": { color: dark ? "#6B7280" : "#94A3B8" },
    },
    pickerList: { flex: 1, overflowY: "auto", padding: 6 },
    pickerOption: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 8,
      cursor: "pointer",
      transition: "background .12s",
      border: "none",
      background: "transparent",
      width: "100%",
      textAlign: "left",
      fontFamily: "inherit",
      "&:hover": { background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.04)" },
    },
    pickerOptionActive: {
      background: dark ? "rgba(99,102,241,0.10)" : "rgba(61,92,253,0.08)",
      "&:hover": {
        background: dark ? "rgba(99,102,241,0.14)" : "rgba(61,92,253,0.12)",
      },
    },
    pickerIconWrap: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.05)",
      color: dark ? "#9CA3AF" : "#64748B",
    },
    pickerOptionText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    pickerOptionName: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#E8EAED" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerOptionMeta: {
      fontSize: 11,
      color: dark ? "#6B7280" : "#94A3B8",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerCheck: { color: dark ? "#A5B4FC" : "#3D5CFD", flexShrink: 0 },
    pickerEmpty: {
      padding: "24px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: dark ? "#6B7280" : "#94A3B8",
    },
    pickerDivider: {
      height: 1,
      margin: "4px 6px",
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)",
    },

    boardWrapper: {
      flex: 1,
      display: "flex",
      overflow: "hidden",
      minHeight: 0,
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      flexDirection: "column",
      gap: theme.spacing(2),
      color: dark ? "rgba(255,255,255,0.5)" : "#64748B",
    },

    settingsDrawer: {
      "& .MuiDrawer-paper": {
        width: "92%",
        maxWidth: 880,
        background: dark ? "#0A0A0A" : "#F8FAFC",
        borderLeft: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        boxShadow: dark
          ? "-24px 0 64px -16px rgba(0,0,0,0.6)"
          : "-24px 0 64px -16px rgba(15,23,42,0.18)",
        display: "flex",
        flexDirection: "column",
      },
    },
    settingsDrawerHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.06)",
      flexShrink: 0,
    },
    settingsDrawerTitle: {
      fontSize: "1.25rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: dark ? "#E8EAED" : "#0F172A",
    },
    settingsDrawerSub: {
      fontSize: 12.5,
      marginTop: 2,
      color: dark ? "#9CA3AF" : "#64748B",
    },
    settingsDrawerCloseBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      color: dark ? "#9CA3AF" : "#475569",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.08)",
      },
    },
    settingsDrawerBody: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(3),
    },

    boardDialog: {
      "& .MuiDialog-paper": {
        borderRadius: 16,
        background: dark ? "rgba(14,16,22,0.98)" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        boxShadow: dark
          ? "0 32px 90px -16px rgba(0,0,0,0.7)"
          : "0 32px 90px -16px rgba(15,23,42,0.20)",
      },
    },
    boardDialogTitle: {
      fontSize: "1.15rem",
      fontWeight: 700,
      color: dark ? "#E8EAED" : "#0F172A",
      padding: "20px 24px 6px",
    },
    boardDialogBody: {
      padding: "8px 24px 20px",
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      },
    },
    boardDialogActions: {
      padding: "12px 20px 20px",
      gap: 8,
    },
    colorPicker: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginTop: 6,
    },
    colorDot: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      cursor: "pointer",
      border: "2px solid transparent",
      transition: "transform .12s, box-shadow .12s",
      "&:hover": { transform: "scale(1.12)" },
    },
    colorDotSelected: {
      boxShadow: dark
        ? "0 0 0 2px #0A0A0A, 0 0 0 4px rgba(99,102,241,0.55)"
        : "0 0 0 2px #FFFFFF, 0 0 0 4px rgba(61,92,253,0.45)",
    },
    fieldLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "#9CA3AF" : "#64748B",
      marginBottom: 6,
      marginTop: 12,
      display: "block",
    },
  };
});

const ResponsibleFilter = ({ value, onChange, users, classes }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);
  const open = Boolean(anchorEl);

  const selected = useMemo(() => {
    if (!value) return null;
    if (value === "unassigned") return { unassigned: true };
    return users.find((u) => String(u.id) === String(value)) || null;
  }, [value, users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
    }
    if (!open) setSearch("");
  }, [open]);

  const handleClose = () => setAnchorEl(null);
  const pick = (v) => {
    onChange(v);
    handleClose();
  };

  const renderTriggerContent = () => {
    if (selected?.unassigned) {
      return (
        <>
          <span className={classes.pickerIconWrap} style={{ width: 22, height: 22 }}>
            <UsersIcon size={13} strokeWidth={1.75} />
          </span>
          <span className={classes.filterLabel}>Sem responsável</span>
        </>
      );
    }
    if (selected) {
      return (
        <>
          <UserAvatar user={selected} size={22} />
          <span className={classes.filterLabel}>{selected.name}</span>
        </>
      );
    }
    return (
      <>
        <span className={classes.pickerIconWrap} style={{ width: 22, height: 22 }}>
          <UsersIcon size={13} strokeWidth={1.75} />
        </span>
        <span className={classes.filterLabel}>Todos os responsáveis</span>
      </>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`${classes.filterTrigger} ${value ? classes.filterTriggerActive : ""}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {renderTriggerContent()}
        {value ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Limpar filtro"
            className={classes.filterClearBtn}
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }
            }}
          >
            <X size={12} strokeWidth={2.25} />
          </span>
        ) : (
          <ChevronDown size={14} style={{ opacity: 0.55 }} />
        )}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        className={classes.pickerPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className={classes.pickerSearchWrap}>
          <Search size={14} className={classes.pickerSearchIcon} />
          <input
            ref={inputRef}
            className={classes.pickerSearchInput}
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleClose();
            }}
          />
        </div>
        <div className={classes.pickerList}>
          <button
            type="button"
            className={`${classes.pickerOption} ${!value ? classes.pickerOptionActive : ""}`}
            onClick={() => pick("")}
          >
            <span className={classes.pickerIconWrap}>
              <UsersIcon size={14} strokeWidth={1.75} />
            </span>
            <div className={classes.pickerOptionText}>
              <span className={classes.pickerOptionName}>Todos os responsáveis</span>
              <span className={classes.pickerOptionMeta}>Sem filtro</span>
            </div>
            {!value && <Check size={15} className={classes.pickerCheck} />}
          </button>

          <button
            type="button"
            className={`${classes.pickerOption} ${value === "unassigned" ? classes.pickerOptionActive : ""}`}
            onClick={() => pick("unassigned")}
          >
            <span className={classes.pickerIconWrap}>
              <UsersIcon size={14} strokeWidth={1.75} style={{ opacity: 0.55 }} />
            </span>
            <div className={classes.pickerOptionText}>
              <span className={classes.pickerOptionName}>Sem responsável</span>
              <span className={classes.pickerOptionMeta}>Cards não atribuídos</span>
            </div>
            {value === "unassigned" && <Check size={15} className={classes.pickerCheck} />}
          </button>

          <div className={classes.pickerDivider} />

          {filtered.length === 0 && search && (
            <div className={classes.pickerEmpty}>Nenhum usuário encontrado.</div>
          )}

          {filtered.map((u) => {
            const isActive = String(value) === String(u.id);
            return (
              <button
                key={u.id}
                type="button"
                className={`${classes.pickerOption} ${isActive ? classes.pickerOptionActive : ""}`}
                onClick={() => pick(u.id)}
              >
                <UserAvatar user={u} size={28} />
                <div className={classes.pickerOptionText}>
                  <span className={classes.pickerOptionName}>{u.name}</span>
                  {u.email && <span className={classes.pickerOptionMeta}>{u.email}</span>}
                </div>
                {isActive && <Check size={15} className={classes.pickerCheck} />}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

const BoardFilter = ({ value, onChange, boards, classes }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);

  const selected = useMemo(
    () => (value ? boards.find((b) => String(b.id) === String(value)) : null),
    [value, boards]
  );

  const pick = (v) => {
    onChange(v);
    handleClose();
  };

  return (
    <>
      <button
        type="button"
        className={`${classes.filterTrigger} ${value ? classes.filterTriggerActive : ""}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                flexShrink: 0,
                background: selected.color,
              }}
            />
            <span className={classes.filterLabel}>{selected.name}</span>
          </>
        ) : (
          <>
            <span className={classes.pickerIconWrap} style={{ width: 22, height: 22 }}>
              <Layers size={13} strokeWidth={1.75} />
            </span>
            <span className={classes.filterLabel}>Visão Geral</span>
          </>
        )}
        {value ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Limpar filtro de board"
            className={classes.filterClearBtn}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
          >
            <X size={12} strokeWidth={2.25} />
          </span>
        ) : (
          <ChevronDown size={14} style={{ opacity: 0.55 }} />
        )}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        className={classes.pickerPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className={classes.pickerList}>
          <button
            type="button"
            className={`${classes.pickerOption} ${!value ? classes.pickerOptionActive : ""}`}
            onClick={() => pick(null)}
          >
            <span className={classes.pickerIconWrap}>
              <Layers size={14} strokeWidth={1.75} />
            </span>
            <div className={classes.pickerOptionText}>
              <span className={classes.pickerOptionName}>Visão Geral</span>
              <span className={classes.pickerOptionMeta}>Prazos de todos os boards</span>
            </div>
            {!value && <Check size={15} className={classes.pickerCheck} />}
          </button>

          <div className={classes.pickerDivider} />

          {boards.map((b) => {
            const isActive = String(value) === String(b.id);
            return (
              <button
                key={b.id}
                type="button"
                className={`${classes.pickerOption} ${isActive ? classes.pickerOptionActive : ""}`}
                onClick={() => pick(b.id)}
              >
                <span
                  className={classes.pickerIconWrap}
                  style={{ background: "transparent" }}
                >
                  <span
                    style={{ width: 11, height: 11, borderRadius: "50%", background: b.color }}
                  />
                </span>
                <div className={classes.pickerOptionText}>
                  <span className={classes.pickerOptionName}>{b.name}</span>
                  <span className={classes.pickerOptionMeta}>
                    {b.leadCount || 0} {(b.leadCount || 0) === 1 ? "card" : "cards"}
                  </span>
                </div>
                {isActive && <Check size={15} className={classes.pickerCheck} />}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

const ExternalLeads = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const {
    statuses,
    boardLeads,
    boardLoading,
    setStatuses,
    setStats,
    setBoardLeads,
    setBoardLoading,
    moveLeadToStatus,
    addBoardLead,
    updateBoardLead,
    deleteBoardLead,
    addLead,
    updateLead,
    deleteLead,
    isBoardCacheValid,
    clearBoardCache,
  } = useExternalLeadsStore();

  const [searchParam, setSearchParam] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingLead, setDeletingLead] = useState(null);
  const [startConversationPhone, setStartConversationPhone] = useState(null);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [createLeadStatusId, setCreateLeadStatusId] = useState(null);

  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardName, setBoardName] = useState("");
  const [boardColor, setBoardColor] = useState("#5B7CFF");
  const [deleteBoardConfirm, setDeleteBoardConfirm] = useState(null);
  const [boardPassword, setBoardPassword] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [assignedUserFilter, setAssignedUserFilter] = useState("");
  const [companyUsers, setCompanyUsers] = useState([]);

  const [viewMode, setViewMode] = useState("kanban"); // "kanban" | "calendar"
  const [calendarBoardFilter, setCalendarBoardFilter] = useState(null); // null = Visão Geral
  const [calendarLeads, setCalendarLeads] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarStatuses, setCalendarStatuses] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setCompanyUsers(data || []);
      } catch (err) { /* ignore */ }
    };
    fetchUsers();
  }, []);

  const fetchBoards = useCallback(async () => {
    try {
      const { data } = await api.get("/crm-boards");
      setBoards(data);
      if (data.length > 0 && !selectedBoardId) {
        const defaultBoard = data.find(b => b.isDefault) || data[0];
        setSelectedBoardId(defaultBoard.id);
      }
    } catch (err) {
      toastError(err);
    }
  }, [selectedBoardId]);

  const fetchBoardLeads = useCallback(async (forceRefresh = false) => {
    if (!selectedBoardId) return;
    if (!forceRefresh && isBoardCacheValid()) return;

    try {
      setBoardLoading(true);
      const { data } = await api.get("/external-leads", {
        params: { limit: 1000, boardId: selectedBoardId },
      });

      const grouped = {};
      (data.leads || []).forEach((lead) => {
        const sid = lead.statusId;
        if (!grouped[sid]) grouped[sid] = [];
        grouped[sid].push(lead);
      });
      setBoardLeads(grouped);
    } catch (err) {
      toastError(err);
    } finally {
      setBoardLoading(false);
    }
  }, [selectedBoardId, isBoardCacheValid, setBoardLeads, setBoardLoading]);

  const fetchStatuses = useCallback(async () => {
    if (!selectedBoardId) return;
    try {
      const { data } = await api.get("/lead-statuses", {
        params: { boardId: selectedBoardId }
      });
      setStatuses(data);
    } catch (err) {
      toastError(err);
    }
  }, [selectedBoardId, setStatuses]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/external-leads/stats");
      setStats(data);
    } catch (err) {
      toastError(err);
    }
  }, [setStats]);

  const fetchCalendarLeads = useCallback(async () => {
    try {
      setCalendarLoading(true);
      const { data } = await api.get("/external-leads/calendar", {
        params: { boardId: calendarBoardFilter || undefined },
      });
      setCalendarLeads(data.leads || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCalendarLoading(false);
    }
  }, [calendarBoardFilter]);

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    if (!selectedBoardId) return;
    clearBoardCache();
    fetchStatuses();
    fetchStats();
    fetchBoardLeads(true);
  }, [selectedBoardId]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    const eventName = `company-${user.companyId}-externalLead`;

    const handleLeadEvent = (data) => {
      if (data.lead && data.lead.boardId && data.lead.boardId !== selectedBoardId) {
        fetchBoards();
        return;
      }

      if (data.action === "create") {
        addLead(data.lead);
        addBoardLead(data.lead);
        fetchStats();
        fetchBoards();
        toast.info(`${i18n.t("externalLeads.toasts.newLead")} ${data.lead.name}`);
      }
      if (data.action === "update") {
        updateLead(data.lead);
        updateBoardLead(data.lead);
        fetchStats();
      }
      if (data.action === "delete") {
        deleteLead(data.leadId);
        deleteBoardLead(data.leadId);
        fetchStats();
        fetchBoards();
      }
    };

    socket.on(eventName, handleLeadEvent);
    return () => {
      socket.off(eventName, handleLeadEvent);
    };
  }, [socketManager, user.companyId, selectedBoardId, fetchStats, fetchBoards, addLead, updateLead, deleteLead, addBoardLead, updateBoardLead, deleteBoardLead]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    const statusEventName = `company-${user.companyId}-lead-status`;

    const handleStatusEvent = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchStatuses();
        fetchStats();
      }
    };

    socket.on(statusEventName, handleStatusEvent);
    return () => {
      socket.off(statusEventName, handleStatusEvent);
    };
  }, [socketManager, user.companyId, fetchStatuses, fetchStats]);

  // Carrega/atualiza os prazos quando entra no calendário ou troca o filtro de board.
  useEffect(() => {
    if (viewMode !== "calendar") return;
    fetchCalendarLeads();
  }, [viewMode, fetchCalendarLeads]);

  // Mantém o calendário sincronizado em tempo real enquanto está aberto.
  // Debounce: uma rajada de eventos (ex.: campanha criando muitos leads) gera
  // um único refetch após a poeira baixar, em vez de um por evento.
  useEffect(() => {
    if (viewMode !== "calendar") return;
    const socket = socketManager.getSocket(user.companyId);
    const eventName = `company-${user.companyId}-externalLead`;
    let timer = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fetchCalendarLeads(), 800);
    };
    socket.on(eventName, handler);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off(eventName, handler);
    };
  }, [viewMode, socketManager, user.companyId, fetchCalendarLeads]);

  const handleRefresh = async () => {
    setRefreshing(true);
    clearBoardCache();
    if (viewMode === "calendar") {
      await Promise.all([fetchCalendarLeads(), fetchBoards()]);
    } else {
      await Promise.all([fetchBoardLeads(true), fetchStatuses(), fetchStats(), fetchBoards()]);
    }
    setRefreshing(false);
  };

  // Abre o card a partir do calendário, carregando os status do board do próprio
  // card (na Visão Geral o card pode ser de outro board que não o selecionado).
  const handleViewLeadFromCalendar = async (lead) => {
    setDetailModalOpen(true);
    // Status do board do card — independente: se falhar, abre com pills vazios,
    // sem descartar o detalhe do lead já carregado.
    api
      .get("/lead-statuses", { params: { boardId: lead.boardId } })
      .then(({ data }) => setCalendarStatuses(data || []))
      .catch(() => setCalendarStatuses([]));
    try {
      const { data } = await api.get(`/external-leads/${lead.id}`);
      setSelectedLead(data);
    } catch (err) {
      toastError(err);
      setSelectedLead(lead);
    }
  };

  const handleViewLead = async (lead) => {
    setDetailModalOpen(true);
    try {
      const { data } = await api.get(`/external-leads/${lead.id}`);
      setSelectedLead(data);
      updateLead(data);
      updateBoardLead(data);
    } catch (err) {
      toastError(err);
      setSelectedLead(lead);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedLead(null);
    if (viewMode === "calendar") {
      setCalendarStatuses([]);
      fetchCalendarLeads();
    }
  };

  const handleUpdateLeadStatus = async (statusId) => {
    if (!selectedLead) return;
    try {
      const { data } = await api.put(`/external-leads/${selectedLead.id}`, {
        ...selectedLead,
        statusId,
      });
      updateLead(data);
      updateBoardLead(data);
      setSelectedLead(data);
      toast.success(i18n.t("externalLeads.toasts.statusUpdated"));
      fetchStats();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteLead = async () => {
    if (!deletingLead) return;
    try {
      await api.delete(`/external-leads/${deletingLead.id}`);
      deleteLead(deletingLead.id);
      deleteBoardLead(deletingLead.id);
      toast.success(i18n.t("externalLeads.toasts.deleted"));
      fetchStats();
      fetchBoards();
    } catch (err) {
      toastError(err);
    }
    setDeletingLead(null);
    setConfirmModalOpen(false);
  };

  const handleDropCard = async (leadId, fromStatusId, toStatusId, targetIndex) => {
    const isSameColumn = fromStatusId === toStatusId;

    const prevBoardLeads = {};
    for (const [sid, arr] of Object.entries(boardLeads)) {
      prevBoardLeads[sid] = [...arr];
    }

    moveLeadToStatus(leadId, fromStatusId, toStatusId, targetIndex);

    try {
      if (!isSameColumn) {
        await api.put(`/external-leads/${leadId}`, { statusId: toStatusId });
      }

      const { boardLeads: currentLeads } = useExternalLeadsStore.getState();
      const columnLeads = currentLeads[toStatusId] || [];
      const orderedIds = columnLeads.map(l => l.id);
      if (orderedIds.length > 1) {
        await api.put('/external-leads/reorder', { orderedIds, statusId: toStatusId });
      }

      if (!isSameColumn) {
        toast.success(i18n.t("externalLeads.toasts.statusUpdated"));
        fetchStats();
      }
    } catch (err) {
      setBoardLeads(prevBoardLeads);
      toastError(err);
    }
  };

  const handleReorderColumns = async (orderedIds) => {
    const reordered = orderedIds.map((id, index) => {
      const s = statuses.find(st => st.id === id);
      return s ? { ...s, order: index + 1 } : null;
    }).filter(Boolean);
    setStatuses(reordered);

    try {
      await api.put("/lead-statuses/reorder", { orderedIds });
    } catch (err) {
      toastError(err);
      fetchStatuses();
    }
  };

  const handleOpenNewBoard = () => {
    setTemplateModalOpen(true);
  };

  const handleCreateBoardFromTemplate = async ({ name, color, password, template }) => {
    try {
      const { data } = await api.post("/crm-boards", {
        name: name.trim(),
        color,
        password,
        template
      });
      setSelectedBoardId(data.id);
      toast.success("Quadro criado");
      setTemplateModalOpen(false);
      fetchBoards();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenEditBoard = (e, board) => {
    e.stopPropagation();
    setEditingBoard(board);
    setBoardName(board.name);
    setBoardColor(board.color);
    setBoardDialogOpen(true);
  };

  const handleSaveBoard = async () => {
    if (!boardName.trim()) return;
    try {
      if (editingBoard) {
        await api.put(`/crm-boards/${editingBoard.id}`, { name: boardName.trim(), color: boardColor });
        toast.success("Quadro atualizado");
      } else {
        if (!boardPassword) {
          toastError({ response: { data: { error: "Digite sua senha para confirmar" } } });
          return;
        }
        const { data } = await api.post("/crm-boards", { name: boardName.trim(), color: boardColor, password: boardPassword });
        setSelectedBoardId(data.id);
        toast.success("Quadro criado");
      }
      setBoardDialogOpen(false);
      setBoardPassword("");
      fetchBoards();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteBoard = async () => {
    if (!deleteBoardConfirm) return;
    if (!boardPassword) {
      toastError({ response: { data: { error: "Digite sua senha para confirmar" } } });
      return;
    }
    try {
      await api.delete(`/crm-boards/${deleteBoardConfirm.id}`, { data: { password: boardPassword } });
      toast.success("Quadro removido");
      setDeleteBoardConfirm(null);
      setBoardPassword("");
      if (deleteBoardConfirm.id === selectedBoardId) {
        setSelectedBoardId(null);
      }
      fetchBoards();
    } catch (err) {
      toastError(err);
      setDeleteBoardConfirm(null);
      setBoardPassword("");
    }
  };

  const selectedBoard = boards.find(b => b.id === selectedBoardId);
  // Os quadros (criar, editar e remover): hoje, admin e super, como a API; com
  // a regra nova, crm.boards.manage. Iniciar conversa pelo telefone do card cria
  // o contato, se novo, e a conversa (contacts.edit e tickets.create): hoje,
  // quem atende (o "Apenas Interno" recebia a recusa).
  const { can } = usePermission();
  const managesBoards = can("crm.boards.manage", isAdminUser(user));
  // Compartilhar o quadro por link externo: crm.boards.share (hoje, admin e
  // super, como a API).
  const sharesBoards = can("crm.boards.share", isAdminUser(user));
  const canStartConversation =
    can("tickets.create", !isInternalOnly(user)) && can("contacts.edit", !isInternalOnly(user));

  return (
    <MainContainer className={classes.root}>
      <ConfirmationModal
        title={i18n.t("externalLeads.confirmDelete.title")}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingLead(null);
        }}
        onConfirm={handleDeleteLead}
      >
        {i18n.t("externalLeads.confirmDelete.message")}
      </ConfirmationModal>

      <Dialog
        open={Boolean(deleteBoardConfirm)}
        onClose={() => { setDeleteBoardConfirm(null); setBoardPassword(""); }}
        maxWidth="xs"
        fullWidth
        className={classes.boardDialog}
      >
        <DialogTitle disableTypography className={classes.boardDialogTitle}>
          Remover Quadro
        </DialogTitle>
        <DialogContent className={classes.boardDialogBody}>
          <Typography style={{ marginBottom: 16, fontSize: 13.5, lineHeight: 1.5 }}>
            Tem certeza que deseja remover o quadro "<strong>{deleteBoardConfirm?.name}</strong>"?
            Esta ação não pode ser desfeita.
          </Typography>
          <TextField
            label="Confirme sua senha"
            type="password"
            value={boardPassword}
            onChange={(e) => setBoardPassword(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            onKeyDown={(e) => { if (e.key === "Enter") handleDeleteBoard(); }}
          />
        </DialogContent>
        <DialogActions className={classes.boardDialogActions}>
          <Button onClick={() => { setDeleteBoardConfirm(null); setBoardPassword(""); }}>Cancelar</Button>
          <Button
            onClick={handleDeleteBoard}
            color="secondary"
            variant="contained"
            disabled={!boardPassword}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <LeadDetailModal
        open={detailModalOpen}
        lead={selectedLead}
        statuses={viewMode === "calendar" ? calendarStatuses : statuses}
        onClose={handleCloseDetailModal}
        onUpdateStatus={handleUpdateLeadStatus}
        onDelete={(lead) => {
          handleCloseDetailModal();
          setDeletingLead(lead);
          setConfirmModalOpen(true);
        }}
        onLeadUpdated={(updatedLead) => {
          setSelectedLead(updatedLead);
          updateLead(updatedLead);
          updateBoardLead(updatedLead);
        }}
      />

      <Drawer
        anchor="right"
        open={settingsDrawerOpen}
        onClose={() => {
          setSettingsDrawerOpen(false);
          fetchStatuses();
          fetchStats();
          fetchBoardLeads(true);
        }}
        className={classes.settingsDrawer}
      >
        <div className={classes.settingsDrawerHeader}>
          <div>
            <Typography className={classes.settingsDrawerTitle}>
              Configurações
            </Typography>
            <Typography className={classes.settingsDrawerSub}>
              Status, tokens de API e preferências do CRM
            </Typography>
          </div>
          <Tooltip title="Fechar">
            <IconButton
              className={classes.settingsDrawerCloseBtn}
              onClick={() => {
                setSettingsDrawerOpen(false);
                fetchStatuses();
                fetchStats();
                fetchBoardLeads(true);
              }}
            >
              <X size={18} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.settingsDrawerBody}>
          <LeadSettingsContent />
        </div>
      </Drawer>

      <CrmActivityLogDrawer
        open={activityLogOpen}
        onClose={() => setActivityLogOpen(false)}
      />

      <BoardTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onConfirm={handleCreateBoardFromTemplate}
      />

      {sharesBoards && (
        <BoardShareModal
          open={shareModalOpen}
          board={selectedBoard}
          onClose={() => setShareModalOpen(false)}
        />
      )}

      <Dialog
        open={boardDialogOpen}
        onClose={() => { setBoardDialogOpen(false); setBoardPassword(""); }}
        maxWidth="xs"
        fullWidth
        className={classes.boardDialog}
      >
        <DialogTitle disableTypography className={classes.boardDialogTitle}>
          {editingBoard ? "Editar Quadro" : "Novo Quadro"}
        </DialogTitle>
        <DialogContent className={classes.boardDialogBody}>
          <Typography className={classes.fieldLabel} style={{ marginTop: 0 }}>
            Nome
          </Typography>
          <TextField
            autoFocus
            placeholder="Ex: Time comercial"
            value={boardName}
            onChange={(e) => setBoardName(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
          <Typography className={classes.fieldLabel}>Cor</Typography>
          <div className={classes.colorPicker}>
            {BOARD_COLORS.map((c) => (
              <div
                key={c}
                className={`${classes.colorDot} ${boardColor === c ? classes.colorDotSelected : ""}`}
                style={{ background: c }}
                onClick={() => setBoardColor(c)}
              />
            ))}
          </div>
          {!editingBoard && (
            <>
              <Typography className={classes.fieldLabel}>Confirme sua senha</Typography>
              <TextField
                type="password"
                value={boardPassword}
                onChange={(e) => setBoardPassword(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Sua senha"
                onKeyDown={(e) => { if (e.key === "Enter" && boardName.trim() && boardPassword) handleSaveBoard(); }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.boardDialogActions}>
          <Button onClick={() => { setBoardDialogOpen(false); setBoardPassword(""); }}>Cancelar</Button>
          <Button
            onClick={handleSaveBoard}
            color="primary"
            variant="contained"
            disabled={!boardName.trim() || (!editingBoard && !boardPassword)}
          >
            {editingBoard ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.eyebrow}>
            <span className={classes.eyebrowDot} />
            Boards
          </span>
          <Typography className={classes.title}>
            {selectedBoard?.name || i18n.t("externalLeads.title")}
          </Typography>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <div className={classes.viewSwitch}>
          <button
            type="button"
            className={`${classes.viewSwitchBtn} ${viewMode === "kanban" ? classes.viewSwitchBtnActive : ""}`}
            onClick={() => setViewMode("kanban")}
          >
            <LayoutGrid size={16} strokeWidth={1.9} />
            Kanban
          </button>
          <button
            type="button"
            className={`${classes.viewSwitchBtn} ${viewMode === "calendar" ? classes.viewSwitchBtnActive : ""}`}
            onClick={() => setViewMode("calendar")}
          >
            <CalendarDays size={16} strokeWidth={1.9} />
            Calendário
          </button>
        </div>
        <Box className={classes.headerActions}>
          {sharesBoards && viewMode === "kanban" && selectedBoard && (
            <Tooltip title={i18n.t("boardShare.open")}>
              <IconButton
                className={`${classes.iconBtn} ${shareModalOpen ? classes.iconBtnActive : ""}`}
                onClick={() => setShareModalOpen(true)}
                aria-label={i18n.t("boardShare.open")}
              >
                <Share2 size={17} strokeWidth={1.75} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Atividades Recentes">
            <IconButton
              className={`${classes.iconBtn} ${activityLogOpen ? classes.iconBtnActive : ""}`}
              onClick={() => setActivityLogOpen(true)}
              aria-label="Atividades recentes"
            >
              <History size={17} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configurações">
            <IconButton
              className={`${classes.iconBtn} ${settingsDrawerOpen ? classes.iconBtnActive : ""}`}
              onClick={() => setSettingsDrawerOpen(true)}
              aria-label="Configurações"
            >
              <Settings size={17} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>
          <Tooltip title={refreshing ? "Atualizando..." : i18n.t("externalLeads.refresh")}>
            <IconButton
              className={classes.iconBtn}
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Atualizar"
            >
              {refreshing ? (
                <CircularProgress size={16} style={{ color: "inherit" }} />
              ) : (
                <RefreshCw size={17} strokeWidth={1.75} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        </div>
      </div>

      {viewMode === "kanban" && (
      <div className={classes.boardTabs}>
        {boards.map((board) => {
          const active = board.id === selectedBoardId;
          return (
            <div
              key={board.id}
              className={`${classes.boardTab} ${active ? classes.boardTabActive : ""}`}
              onClick={() => setSelectedBoardId(board.id)}
              role="tab"
              tabIndex={0}
              aria-selected={active}
            >
              <div className={classes.boardTabDot} style={{ background: board.color }} />
              <span>{board.name}</span>
              <span className={classes.boardTabCount}>{board.leadCount || 0}</span>
              {active && managesBoards && (
                <div className={classes.boardTabAdminIcons}>
                  <Tooltip title="Editar quadro">
                    <span
                      className={classes.boardTabIcon}
                      onClick={(e) => handleOpenEditBoard(e, board)}
                    >
                      <Pencil size={13} strokeWidth={1.75} />
                    </span>
                  </Tooltip>
                  {!board.isDefault && (
                    <Tooltip title="Remover quadro">
                      <span
                        className={`${classes.boardTabIcon} ${classes.boardTabIconDanger}`}
                        onClick={(e) => { e.stopPropagation(); setDeleteBoardConfirm(board); }}
                      >
                        <Trash2 size={13} strokeWidth={1.75} />
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {managesBoards && (
          <button
            type="button"
            className={classes.addBoardBtn}
            onClick={handleOpenNewBoard}
          >
            <Plus size={14} strokeWidth={2} />
            Novo quadro
          </button>
        )}
      </div>
      )}

      <div className={classes.toolbar}>
        <div className={classes.searchWrap}>
          <Search size={15} className={classes.searchIcon} strokeWidth={1.75} />
          <TextField
            placeholder="Buscar por nome, telefone, e-mail, OS..."
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
            className={classes.searchField}
            variant="outlined"
            size="small"
            inputProps={{ "aria-label": "Buscar card" }}
          />
          {searchParam && (
            <button
              type="button"
              className={classes.searchClearBtn}
              onClick={() => setSearchParam("")}
              aria-label="Limpar busca"
            >
              <X size={13} strokeWidth={2.25} />
            </button>
          )}
        </div>

        <ResponsibleFilter
          value={assignedUserFilter}
          onChange={setAssignedUserFilter}
          users={companyUsers}
          classes={classes}
        />

        {viewMode === "calendar" && (
          <BoardFilter
            value={calendarBoardFilter}
            onChange={setCalendarBoardFilter}
            boards={boards}
            classes={classes}
          />
        )}
      </div>

      <div className={classes.boardWrapper}>
        {viewMode === "calendar" ? (
          <CrmCalendarView
            events={calendarLeads}
            loading={calendarLoading}
            searchParam={searchParam}
            assignedUserFilter={assignedUserFilter}
            onSelectLead={handleViewLeadFromCalendar}
          />
        ) : boardLoading && Object.keys(boardLeads).length === 0 ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={32} />
            <Typography>{i18n.t("externalLeads.loading")}</Typography>
          </div>
        ) : (
          <KanbanBoard
            boardId={selectedBoardId}
            statuses={statuses}
            boardLeads={boardLeads}
            searchParam={searchParam}
            assignedUserFilter={assignedUserFilter}
            onDropCard={handleDropCard}
            onReorderColumns={handleReorderColumns}
            onViewLead={handleViewLead}
            onDeleteLead={(lead) => {
              setDeletingLead(lead);
              setConfirmModalOpen(true);
            }}
            onStartConversation={canStartConversation ? (phone) => setStartConversationPhone(phone) : undefined}
            onAddLead={(statusId) => setCreateLeadStatusId(statusId)}
            onStatusesChange={() => {
              fetchStatuses();
              fetchBoardLeads(true);
            }}
          />
        )}
      </div>

      <StartConversationModal
        open={Boolean(startConversationPhone)}
        onClose={() => setStartConversationPhone(null)}
        phoneNumber={startConversationPhone || ""}
      />

      <CreateLeadModal
        open={createLeadStatusId !== null}
        onClose={() => setCreateLeadStatusId(null)}
        statusId={createLeadStatusId}
        boardId={selectedBoardId}
      />
    </MainContainer>
  );
};

export default ExternalLeads;
